/**
 * CirurgiasQuantityCell - Editable quantity cell with +/- buttons
 */
import React, { useState, useEffect } from 'react';

interface QuantityCellProps {
  value: number;
  itemId: number;
  isReadOnly: boolean;
  onUpdate: (itemId: number, newQuantity: number) => void;
}

export const CirurgiasQuantityCell: React.FC<QuantityCellProps> = ({
  value,
  itemId,
  isReadOnly,
  onUpdate,
}) => {
  const [localValue, setLocalValue] = useState(value.toString());
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleCommit = async (newValueStr: string) => {
    const numValue = parseFloat(newValueStr);
    if (!isNaN(numValue) && numValue !== value && numValue >= 0) {
      setIsUpdating(true);
      await onUpdate(itemId, numValue);
      setIsUpdating(false);
    } else if (isNaN(numValue) || numValue < 0) {
      setLocalValue(value.toString());
    }
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = (parseFloat(localValue) || 0) + 1;
    setLocalValue(newValue.toString());
    handleCommit(newValue.toString());
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    const current = parseFloat(localValue) || 0;
    const newValue = Math.max(0, current - 1);
    setLocalValue(newValue.toString());
    handleCommit(newValue.toString());
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    handleCommit(localValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCommit(localValue);
      (e.target as HTMLInputElement).blur();
    }
  };

  if (isReadOnly) {
    return (
      <div className="px-2 py-1 text-sm text-center">
        {value}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={handleDecrement}
        disabled={isUpdating}
        className="w-7 h-7 flex items-center justify-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-bold text-base transition-colors focus:outline-none disabled:opacity-50"
        title="Diminuir (-1)"
      >
        −
      </button>

      <input
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isUpdating}
        className="w-16 h-7 px-1 text-center text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 disabled:opacity-50"
        min="0"
        step="1"
      />

      <button
        type="button"
        onClick={handleIncrement}
        disabled={isUpdating}
        className="w-7 h-7 flex items-center justify-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-bold text-base transition-colors focus:outline-none disabled:opacity-50"
        title="Aumentar (+1)"
      >
        +
      </button>
    </div>
  );
};
